import { FormEvent } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Stack,
  CircularProgress,
} from "@mui/material";
import { useAtom } from "jotai";
import { useCreateAnnotationMutation } from "api/virtual-tour/mutations/useCreateAnnotationMutation";
import { newAnnotationAtom } from "store/atoms/newAnnotation";
import CommentEditor from "components/CommentEditor/CommentEditor";
import { useGetWorkspaceUsersQuery } from "api/workspace/queries/useGetWorkspaceUsersQuery";
import { useWorkspaces } from "contexts/WorkspacesContext";
import { toast } from "react-toastify";
import { vtourAnnotationsLengthAtom } from "store/atoms/vtourAnnotationsLengthAtom";
import { NewComment } from "./NewComment";

type NewAnnotationDialogProps = Omit<
  DialogProps,
  "title" | "onSubmit" | "open"
> & {
  tourId: string;
  onClose: () => void;
};

export const NewAnnotationDialog = ({
  tourId,
  onClose,
  ...dialogProps
}: NewAnnotationDialogProps) => {
  const { mutate: createAnnotation, isPending } = useCreateAnnotationMutation();
  const [newAnnotation, setNewAnnotation] = useAtom(newAnnotationAtom);
  const {
    activeWorkspace: { id: workspaceId },
  } = useWorkspaces();
  const { data: workspaceUsers } = useGetWorkspaceUsersQuery(workspaceId);
  const [vtourAnnotationsLength, setVtourAnnotationsLength] = useAtom(vtourAnnotationsLengthAtom);

  if (!newAnnotation) return null;

  const handleSubmit = (formattedText: string, mentions: string[], cleanMessage: string, files?: File[]) => {
    if (!newAnnotation.position) return;

    createAnnotation(
      {
        position: newAnnotation.position,
        tourId,
        type: "TEXT",
        metadata: {
          attachments: files?.map(file => ({
            filename: file.name || ""
          })) || [],
        },
        message: formattedText,
        cleanMessage: cleanMessage,
        mentions,
      },
      {
        onSuccess: () => {
          setNewAnnotation(null);
          toast("Annotation added successfully!", {
            type: "success",
          });
          setVtourAnnotationsLength(prev => prev + 1);
          onClose();
        },
        onError: (err) => {
          console.error("Failed to create annotation", err);
          toast("Failed to create annotation", {
            type: "error",
          });
        },
      }
    );
  };

  return (
    <Dialog
      {...dialogProps}
      open={Boolean(newAnnotation?.position)}
      onClose={() => {
        setNewAnnotation(null);
        onClose();
      }}
      disablePortal
      fullWidth
    >
      <DialogTitle sx={{
        backgroundColor: '#212121',
        color: 'white',
      }}>Add Annotation</DialogTitle>
      <DialogContent sx={{
        backgroundColor: '#212121',
        color: 'white',
      }}>
        <Stack p={2}>
          <NewComment annotation={newAnnotation} type="annotation" onClose={onClose} tourId={tourId} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{
        backgroundColor: '#212121',
        color: 'white',
      }}>
        <Button
          onClick={() => {
            setNewAnnotation(null);
            onClose();
          }}
          disabled={isPending}
          sx={{ color: 'white' }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
